import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button, deleteIconButtonClass } from "@/components/ui/button";
import { ArrowDown, ArrowUp, Trash2, Eye, X, Plus, Download, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { Label } from "@/components/ui/label";
import { CurrencyInput } from "@/components/ui/currency-input";
import { DateInput } from "@/components/ui/date-input";
import { MonthInput } from "@/components/ui/month-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Factura, opcionesAPI, type Sucursal, type VentaRemitoOpcionesDeposito, ventaRemitosAPI, ventasAPI } from "@/lib/api";
import { clearDocFocus, peekDocFocus, subscribeDocFocus } from "@/lib/docFocus";
import {
  METODOS_PAGO_FACTURA_FALLBACK,
  metodoInicial,
  opcionesDesdeCatalogo,
  reasignarCodigoCatalogo,
  type MetodoPagoOpcion,
} from "./metodoPagoCatalogo";
import { openPdfTab } from "@/lib/openPdfTab";
import { RemitoOperatorBadges } from "@/lib/RemitoOperatorBadges";
import type { UserData } from "@/components/LoginPage";
import {
  clientePermiteCae,
  normalizeClienteTiposPermitidos,
} from "@/lib/clienteTipoFactura";
import { dataRefresh } from "@/lib/dataRefresh";
import { sessionIsElevated } from "@/lib/modulePermissions";
import { pickDefaultPuntoVenta } from "@/lib/pvAsignado";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { LoadingOverlay } from "@/components/ui/loading-overlay";
import { formatDateOnlyEsAR, roundMoney } from "@/lib/utils";
import { roundToPeso } from "@/lib/roundToPeso";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import {
  carouselChipDays,
  DEFAULT_FACTURA_PAGE_SIZE,
  FACTURA_PAGE_SIZE_OPTIONS,
  monthValueToFechaRange,
  normalizeCierreDiasPayload,
  previousMonthValue,
  todayYmd,
  type CierreDiaChip,
} from "./cierreCarousel";
import { CierreDiasCarousel } from "./CierreDiasCarousel";
import {
  FECHA_VENTA_MODAL_TITULO,
  mensajeSeCrearaConFecha,
  nextFechaVentaYmd,
  ventaRequiereAvisoFecha,
} from "./fechaVentaCierre";
import {
  buildNcItemMap,
  ncCantidadPendienteEntrega,
  ncCantidadRestante,
  ncCantidadSugerida,
  ncComboPendiente,
  ncComboRestante,
  ncComboSugerida,
  ncItemIds,
  ncPayloadFromSelection,
  ncVisualLineSubtotal,
  patchNcVisualSelection,
} from "./ncCantidadPrefill";
import { NC_SALDO_CF_COPY, ncSaldoToggleEnabled } from "./ncSaldoToggle";
import {
  facturaEstadoLabel,
  sortFacturaRows,
  toggleFacturaSort,
  type FacturaSortKey,
  type SortDirection,
} from "./facturaListSort";
import {
  comboVisualNombre,
  comboVisualNombrePdf,
  comboVisualPersistedSaveId,
  comboVisualPrecioSum,
  groupComboVisualRows,
} from "@/lib/comboLine";
import {
  buildAsignacionPlan,
  canConfirmAsignacion,
  clienteEsDeudor,
  countLabel,
  productsLabel,
  sharedDepDetail,
  mapAsignacionToRemitoEntrega,
  type AsignacionPlan,
} from "./remitoAsignacionAgrupada";

interface FacturacionListProps {
  facturas: Factura[];
  user?: UserData;
  onConfirmarFactura: (id: number, tipo: "cae" | "negro", paymentData: any, facturaTipo: "A" | "B" | "X") => Promise<void>;
  onEliminarFactura: (id: number) => void;
  onGenerarNC?: (facturaId: number, esCreditoCliente: boolean, items?: Array<{ id: number; cantidad: number }>) => Promise<void>;
  onGenerarNCX?: (facturaId: number, esCreditoCliente: boolean, items?: Array<{ id: number; cantidad: number }>) => Promise<void>;
  onReconciliarFactura?: (facturaId: number) => Promise<void>;
  cierres?: any[];
  isReadOnly?: boolean;
  mode?: "operativo" | "historial";
  showTotalColumn?: boolean;
}

/** Override Producto (nombre PDF): elevated (is_superuser o Super Usuario). */
function canEditFacturaItemNombre(user: UserData | undefined): boolean {
  return sessionIsElevated(user);
}

/** Nombre de línea: API camelCase `nombreProducto` (bug Ver mostraba «Producto»). */
function facturaItemNombre(item: Record<string, any> | null | undefined): string {
  if (!item) return "Producto";
  return (
    item.nombreProducto ||
    item.nombre_producto ||
    item.producto_nombre ||
    item.producto?.nombre ||
    item.nombre ||
    "Producto"
  );
}

function facturaEditNombresMap(items: any[] | undefined | null): Record<number, string> {
  const nombres: Record<number, string> = {};
  for (const row of groupComboVisualRows(items || [])) {
    const saveId = comboVisualPersistedSaveId(row);
    if (saveId == null) continue;
    nombres[saveId] = row.isCombo
      ? comboVisualNombrePdf(row.legs)
      : facturaItemNombre(row.legs[0] as any);
  }
  return nombres;
}

interface PaymentMethod {
  id: string;
  metodoPago: string;
  metodoCodigo?: string;
  /** Texto del dropdown. Solo lo imprime el PDF. El cierre sigue con metodoPago. */
  metodoTipo?: string;
  importe: number;
  importeUSD?: number;
  tasaCambio?: number;
  referencia?: string;
  chequeTipo?: string;
  chequeNumero?: string;
  chequeBanco?: string;
  chequeFecha?: string;
  chequeFechaCreacion?: string;
  chequeOrigen?: string;
}

function metodoRequiereReferencia(metodo: string): boolean {
  const s = String(metodo || '').toLowerCase();
  return s.includes('transfer') || s.includes('tarjeta');
}

/** Caja idéntica CAE/FX: mismo h/w, padding y borde 1px. El color sí distingue. */
const facturaFinalizeBtnBox =
  "h-8 w-12 px-0 text-xs font-medium border shadow-xs";

/**
 * CAE: reposo outline verde muted. Hover con `!` para ganar a
 * Button variant="outline" (`hover:bg-accent hover:text-accent-foreground`).
 */
const facturaCaeBtnClass =
  `${facturaFinalizeBtnBox} border-emerald-700/55 text-emerald-700 bg-emerald-50/80 hover:!bg-emerald-600 hover:!text-white hover:!font-bold hover:!border-emerald-600 dark:hover:!bg-emerald-600 dark:hover:!text-white dark:hover:!border-emerald-600`;

async function hydrateFactura(factura: Factura): Promise<Factura> {
  if (!factura.id) return factura;
  try {
    return await ventasAPI.getFactura(factura.id);
  } catch {
    return factura;
  }
}

export function FacturacionList({
  facturas: _facturasCache,
  user,
  onConfirmarFactura,
  onEliminarFactura,
  onGenerarNC,
  onGenerarNCX,
  onReconciliarFactura,
  cierres: _cierresCache = [],
  isReadOnly = false,
  mode = "operativo",
  showTotalColumn = true,
}: FacturacionListProps) {
  const canEditItemNombre = canEditFacturaItemNombre(user);
  const elevated = sessionIsElevated(user);
  const seeTotal = elevated || showTotalColumn;
  const isHistorial = mode === "historial";
  const today = todayYmd();
  const operatorSucursalId = pickDefaultPuntoVenta(user)?.sucursal || null;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_FACTURA_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState<Factura[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [selectedFecha, setSelectedFecha] = useState(today);
  const chipSelectionInited = useRef(false);
  const [cierreDias, setCierreDias] = useState<CierreDiaChip[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [sucursalId, setSucursalId] = useState<string>("");
  const [historialMonth, setHistorialMonth] = useState(previousMonthValue(today));
  const [confirmingFactura, setConfirmingFactura] = useState<string | null>(null);
  const [fechaModalOpen, setFechaModalOpen] = useState(false);
  const [fechaAvisoYmd, setFechaAvisoYmd] = useState("");
  const [pendingConfirmPayload, setPendingConfirmPayload] = useState<{
    paymentData: any;
    tipo: "cae" | "negro";
    facturaTipo: "A" | "B" | "X";
    facturaId: number;
  } | null>(null);
  const [reconcilingFactura, setReconcilingFactura] = useState<number | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState<Factura | null>(null);
  const [confirmType, setConfirmType] = useState<"cae" | "negro">("cae");
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [metodosPagoOpciones, setMetodosPagoOpciones] = useState<MetodoPagoOpcion[]>(
    METODOS_PAGO_FACTURA_FALLBACK,
  );
  const metodosPagoOpcionesRef = useRef(metodosPagoOpciones);
  metodosPagoOpcionesRef.current = metodosPagoOpciones;
  const [showProductsDialog, setShowProductsDialog] = useState(false);
  const [viewingFactura, setViewingFactura] = useState<Factura | null>(null);
  const [editNombres, setEditNombres] = useState<Record<number, string>>({});
  const [savingNombreItemId, setSavingNombreItemId] = useState<number | null>(null);
  const [selectedFacturaTipo, setSelectedFacturaTipo] = useState<"A" | "B" | "X">("B");
  const [facturaToDelete, setFacturaToDelete] = useState<Factura | null>(null);
  const [enviarACuentaCorriente, setEnviarACuentaCorriente] = useState(false);
  /** ON: sobrante (pago de más) → saldo a favor. OFF: cambio, no deja crédito. */
  const [acreditarResto, setAcreditarResto] = useState(false);
  /** F3: atajo factura + remito finalizado. Default OFF. */
  const [generarRemitoAuto, setGenerarRemitoAuto] = useState(false);
  const [showAsignacionModal, setShowAsignacionModal] = useState(false);
  const [asignacionOpciones, setAsignacionOpciones] = useState<VentaRemitoOpcionesDeposito | null>(null);
  const [asignacionSelectedDep, setAsignacionSelectedDep] = useState<number | null>(null);
  const [asignacionSaving, setAsignacionSaving] = useState(false);
  const [pendingAutoRemito, setPendingAutoRemito] = useState<{
    paymentData: any;
    tipo: "cae" | "negro";
    facturaTipo: "A" | "B" | "X";
    facturaId: number;
  } | null>(null);
  const [facturaForNC, setFacturaForNC] = useState<Factura | null>(null);
  const [generatingNC, setGeneratingNC] = useState(false);
  const [ncEsCreditoCliente, setNcEsCreditoCliente] = useState(false);
  const ncSaldoHabilitado = ncSaldoToggleEnabled(facturaForNC);
  const confirmarSaldoHabilitado = ncSaldoToggleEnabled(selectedFactura);
  const [ncSelectedItems, setNcSelectedItems] = useState<Record<number, { selected: boolean; cantidad: number }>>({});
  const [ncIsPartial, setNcIsPartial] = useState(false);
  const [ncInterna, setNcInterna] = useState(false);
  /** IDs con NC exitosa en esta sesión (evita re-habilitar el botón si un refresh stale pisa tieneNotaCredito). */
  const [ncEmitidasIds, setNcEmitidasIds] = useState<Set<number>>(() => new Set());
  const [ncXEmitidasIds, setNcXEmitidasIds] = useState<Set<number>>(() => new Set());
  const [sortKey, setSortKey] = useState<FacturaSortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  // Saldo application state
  const [clienteSaldo, setClienteSaldo] = useState(0);  // Client's current saldo (positive = a favor)
  const [aplicarSaldo, setAplicarSaldo] = useState(false);  // Whether to apply saldo
  const saldoAplicado = aplicarSaldo && clienteSaldo > 0 ? Math.min(clienteSaldo, selectedFactura?.total || 0) : 0;

  const effectiveSucursalId = ((): number | null => {
    if (isHistorial || elevated) {
      const n = Number(sucursalId);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    return operatorSucursalId;
  })();

  const chipDays = carouselChipDays(
    cierreDias,
    today,
    effectiveSucursalId,
  );
  const chipDaysKey = chipDays.join(",");

  const handleChipSelect = (ymd: string) => {
    setSelectedFecha(ymd);
  };

  useEffect(() => {
    if (isHistorial || !chipDaysKey) return;
    const days = chipDaysKey.split(",");
    const last = days[days.length - 1]!;
    if (!days.includes(selectedFecha)) {
      setSelectedFecha(last);
      chipSelectionInited.current = true;
      return;
    }
    if (!chipSelectionInited.current) {
      chipSelectionInited.current = true;
      if (last > today) setSelectedFecha(last);
    }
  }, [isHistorial, chipDaysKey, selectedFecha, today]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(searchQuery), 300);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [searchDebounced, selectedFecha, pageSize, sucursalId, historialMonth, isHistorial]);

  useEffect(() => {
    if (!elevated && !isHistorial) return;
    let cancelled = false;
    opcionesAPI.listSucursales().then((list) => {
      if (cancelled) return;
      const active = (list || []).filter((s) => s.activo !== false);
      setSucursales(active);
      setSucursalId((prev) => {
        if (prev) return prev;
        if (isHistorial) return "";
        const first = active.find((s) => s.id != null);
        return first?.id != null ? String(first.id) : "";
      });
    }).catch(() => {
      if (!cancelled) setSucursales([]);
    });
    return () => {
      cancelled = true;
    };
  }, [elevated, isHistorial]);

  const loadCierreDias = useCallback(async () => {
    if (isHistorial) return;
    if (elevated && !effectiveSucursalId) return;
    const year = Number(today.slice(0, 4));
    const month = Number(today.slice(5, 7));
    try {
      const dias = await ventasAPI.listCierreDias({
        year,
        month,
        ...(effectiveSucursalId ? { sucursal: effectiveSucursalId } : {}),
      });
      setCierreDias(normalizeCierreDiasPayload(dias));
    } catch {
      setCierreDias([]);
    }
  }, [isHistorial, today, effectiveSucursalId, elevated]);

  useEffect(() => {
    void loadCierreDias();
  }, [loadCierreDias]);

  const loadPage = useCallback(async () => {
    if (!isHistorial && elevated && !effectiveSucursalId) return;
    setListLoading(true);
    try {
      const params: Parameters<typeof ventasAPI.listFacturasPage>[0] = {
        page,
        page_size: pageSize,
        search: searchDebounced.trim() || undefined,
      };
      if (effectiveSucursalId) params.sucursal = effectiveSucursalId;
      if (isHistorial) {
        const range = monthValueToFechaRange(historialMonth);
        params.historial = 1;
        params.month = historialMonth;
        if (range) {
          params.fecha__gte = range.fecha__gte;
          params.fecha__lte = range.fecha__lte;
        }
      } else {
        params.fecha = selectedFecha;
      }
      const data = await ventasAPI.listFacturasPage(params);
      setRows(data.results || []);
      setTotalCount(data.count || 0);
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 403) {
        toast.error("No tiene permiso para consultar el historial de ventas.");
      } else {
        toast.error("Error al cargar facturas");
      }
      setRows([]);
      setTotalCount(0);
    } finally {
      setListLoading(false);
    }
  }, [
    page,
    pageSize,
    searchDebounced,
    selectedFecha,
    effectiveSucursalId,
    isHistorial,
    historialMonth,
    elevated,
  ]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useEffect(() => {
    const onVentas = () => {
      void loadPage();
      void loadCierreDias();
    };
    dataRefresh.on("ventas", onVentas);
    dataRefresh.on("facturas", onVentas);
    return () => {
      dataRefresh.off("ventas", onVentas);
      dataRefresh.off("facturas", onVentas);
    };
  }, [loadPage, loadCierreDias]);

  useEffect(() => {
    let cancel = false;
    opcionesAPI.listMetodosPago()
      .then((rows) => {
        if (cancel) return;
        const opciones = opcionesDesdeCatalogo(rows || []);
        if (opciones.length === 0) return;
        setMetodosPagoOpciones(opciones);
        setPaymentMethods((prev) => prev.map((pm) => {
          const codigo = reasignarCodigoCatalogo(pm.metodoCodigo, pm.metodoPago, opciones);
          if (!codigo || codigo === pm.metodoCodigo) return pm;
          const op = opciones.find((o) => o.codigo === codigo);
          return { ...pm, metodoCodigo: codigo, metodoTipo: op?.tipo || pm.metodoTipo };
        }));
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, []);

  const openPaymentFrom = (factura: Factura, tipo: "cae" | "negro") => {
    setSelectedFactura(factura);
    setConfirmType(tipo);

    if (tipo === "cae") {
      const cliente: any = factura.cliente;
      const allowedTypes = normalizeClienteTiposPermitidos(cliente?.tipoFacturaPermitida);
      if (allowedTypes.includes("A")) setSelectedFacturaTipo("A");
      else if (allowedTypes.includes("B")) setSelectedFacturaTipo("B");
      else setSelectedFacturaTipo("B");
    } else {
      setSelectedFacturaTipo("X");
    }

    const cliente: any = factura.cliente;
    const currentSaldo = Number(cliente?.saldo) || 0;
    const facturaTotal = Number(factura.total) || 0;
    const originalSaldo = currentSaldo;
    setClienteSaldo(originalSaldo);
    setAplicarSaldo(originalSaldo > 0);
    setEnviarACuentaCorriente(false);
    setAcreditarResto(false);
    setGenerarRemitoAuto(false);
    setShowAsignacionModal(false);
    setAsignacionOpciones(null);
    setAsignacionSelectedDep(null);
    setPendingAutoRemito(null);

    const effectiveSaldo = originalSaldo > 0 ? Math.min(originalSaldo, facturaTotal) : 0;
    const initialPaymentAmount = facturaTotal - effectiveSaldo;

    const inicial = metodoInicial(metodosPagoOpcionesRef.current);
    setPaymentMethods([{
      id: Math.random().toString(),
      metodoPago: inicial.metodoPago,
      metodoCodigo: inicial.metodoCodigo,
      metodoTipo: inicial.metodoTipo,
      importe: initialPaymentAmount > 0 ? initialPaymentAmount : 0
    }]);
    setShowPaymentDialog(true);
  };

  const handleOpenPaymentDialog = async (factura: Factura, tipo: "cae" | "negro") => {
    const full = await hydrateFactura(factura);
    openPaymentFrom(full, tipo);
  };

  const handleViewProducts = async (factura: Factura) => {
    const full = await hydrateFactura(factura);
    setViewingFactura(full);
    setEditNombres(facturaEditNombresMap(full.items || []));
    setShowProductsDialog(true);
  };

  const facturaFocusToken = useRef<number | null>(null);
  useEffect(() => {
    const abrir = () => {
      const focus = peekDocFocus();
      if (!focus || focus.kind !== "factura") return;
      if (facturaFocusToken.current === focus.token) return;
      facturaFocusToken.current = focus.token;
      const token = focus.token;
      void ventasAPI.getFactura(focus.id).then((full) => {
        if (facturaFocusToken.current !== token) return;
        clearDocFocus(token);
        setViewingFactura(full);
        setEditNombres(facturaEditNombresMap(full.items || []));
        setShowProductsDialog(true);
      }).catch(() => {
        toast.error("No se pudo abrir la factura.");
      });
    };
    abrir();
    return subscribeDocFocus(abrir);
  }, []);

  const handleSaveItemNombre = async (itemId: number) => {
    if (!viewingFactura?.id || !canEditItemNombre) return;
    const nombre = (editNombres[itemId] ?? "").trim();
    if (!nombre) {
      toast.error("El nombre del artículo no puede estar vacío");
      return;
    }
    const original = facturaEditNombresMap(viewingFactura.items || [])[itemId];
    if (nombre === original) return;
    setSavingNombreItemId(itemId);
    try {
      const updated = await ventasAPI.updateFacturaItemNombre(
        viewingFactura.id,
        itemId,
        nombre,
      );
      setViewingFactura(updated);
      setEditNombres(facturaEditNombresMap(updated.items || []));
      dataRefresh.emit("ventas");
      toast.success("Nombre del artículo actualizado");
    } catch (e: any) {
      toast.error(e?.message || "No se pudo actualizar el nombre");
    } finally {
      setSavingNombreItemId(null);
    }
  };

  const handleDownloadPDF = async (facturaId: number | undefined, _numero: string, _tipoComprobante?: string) => {
    if (!facturaId) return;
    try {
      const blob = await ventasAPI.downloadFacturaPDF(facturaId);
      if (blob) {
        const win = await openPdfTab(blob, undefined, { path: `/api/facturas/${facturaId}/pdf/` });
        if (!win) {
          toast.error("Pop-up bloqueado. Por favor, habilite los pop-ups para este sitio.");
        } else {
          toast.success("PDF abierto en una nueva pestaña");
        }
      } else {
        toast.error("No se pudo generar el PDF");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error descargando PDF");
    }
  };

  const addPaymentMethod = () => {
    const remainingAmount = calculateRemainingAmount();
    if (remainingAmount <= 0 && !acreditarResto) {
      toast.error("El total ya está cubierto");
      return;
    }

    const inicial = metodoInicial(metodosPagoOpcionesRef.current);
    setPaymentMethods([...paymentMethods, {
      id: Math.random().toString(),
      metodoPago: inicial.metodoPago,
      metodoCodigo: inicial.metodoCodigo,
      metodoTipo: inicial.metodoTipo,
      importe: remainingAmount > 0 ? remainingAmount : 0
    }]);
  };

  const removePaymentMethod = (id: string) => {
    if (paymentMethods.length === 1) {
      toast.error("Debe haber al menos un método de pago");
      return;
    }
    setPaymentMethods(paymentMethods.filter(pm => pm.id !== id));
  };

  const getRemainingForPaymentLine = (lineId: string, methods: PaymentMethod[]) => {
    if (!selectedFactura) return 0;
    const totalPaidOthers = methods
      .filter((pm) => pm.id !== lineId)
      .reduce((sum, pm) => sum + roundToPeso(pm.importe || 0), 0);
    const effectiveTotal = roundToPeso(selectedFactura.total - saldoAplicado);
    return Math.max(0, roundToPeso(effectiveTotal - totalPaidOthers));
  };

  const syncDolaresAmounts = (
    pm: PaymentMethod,
    field: string,
    arsAmount: number,
    rate: number,
    usdAmount?: number
  ): PaymentMethod => {
    const roundedRate = roundMoney(rate);
    if (roundedRate <= 0 || !Number.isFinite(roundedRate)) return pm;

    if (field === 'importeUSD' && usdAmount !== undefined) {
      const roundedUsd = roundToPeso(usdAmount);
      return {
        ...pm,
        tasaCambio: roundedRate,
        importe: roundToPeso(roundedUsd * roundedRate),
        importeUSD: roundedUsd,
      };
    }

    if (arsAmount > 0) {
      const roundedArs = roundToPeso(arsAmount);
      return {
        ...pm,
        tasaCambio: roundedRate,
        importe: roundedArs,
        importeUSD: roundToPeso(roundedArs / roundedRate),
      };
    }

    return { ...pm, tasaCambio: roundedRate };
  };

  /** Forward (USD×tasa) o inverso (ARS÷tasa); importes al peso, tasa a 2 decimales. */
  const isDolaresAmountsConsistent = (pm: PaymentMethod): boolean => {
    const rate = roundMoney(pm.tasaCambio);
    const usd = roundToPeso(pm.importeUSD);
    const ars = roundToPeso(pm.importe);
    if (rate <= 0 || usd <= 0 || ars <= 0) return false;

    const arsFromUsd = roundToPeso(usd * rate);
    if (Math.abs(ars - arsFromUsd) <= 0.01) return true;

    const usdFromArs = roundToPeso(ars / rate);
    return Math.abs(usd - usdFromArs) <= 0.01;
  };

  const normalizeDolaresPaymentForSubmit = (pm: PaymentMethod): PaymentMethod => {
    if (String(pm.metodoPago).toLowerCase() !== 'dolares') return pm;

    const rate = roundMoney(pm.tasaCambio);
    const usd = roundToPeso(pm.importeUSD);
    const ars = roundToPeso(pm.importe);
    if (rate <= 0 || usd <= 0) {
      return { ...pm, tasaCambio: rate, importeUSD: usd, importe: ars };
    }

    const arsFromUsd = roundToPeso(usd * rate);
    if (Math.abs(ars - arsFromUsd) <= 0.01) {
      return { ...pm, tasaCambio: rate, importeUSD: usd, importe: arsFromUsd };
    }

    const usdFromArs = roundToPeso(ars / rate);
    if (Math.abs(usd - usdFromArs) <= 0.01) {
      return { ...pm, tasaCambio: rate, importeUSD: usdFromArs, importe: ars };
    }

    return { ...pm, tasaCambio: rate, importeUSD: usd, importe: ars };
  };

  const updatePaymentMethod = (
    id: string,
    field: string,
    value: any,
    metodoCodigo?: string,
    metodoTipo?: string,
  ) => {
    setPaymentMethods((prev) =>
      prev.map((pm) => {
        if (pm.id !== id) return pm;

        let updated: PaymentMethod = {
          ...pm,
          [field]: field === 'tasaCambio' ? roundMoney(value) : value,
          ...(metodoCodigo !== undefined ? { metodoCodigo } : {}),
          ...(metodoTipo !== undefined ? { metodoTipo } : {}),
        };

        if (field === 'metodoPago' && value === 'cheque') {
          updated.chequeOrigen = 'Terceros';
        }

        if (field === 'metodoPago' && value === 'dolares') {
          if (!updated.importe || updated.importe <= 0) {
            const remaining = getRemainingForPaymentLine(id, prev.map((p) => (p.id === id ? updated : p)));
            if (remaining > 0) updated.importe = remaining;
          }
        }

        if (updated.metodoPago === 'dolares') {
          const rate = field === 'tasaCambio' ? roundMoney(value) : roundMoney(updated.tasaCambio || 0);

          if (field === 'tasaCambio') {
            updated = syncDolaresAmounts(updated, field, updated.importe || 0, rate);
          } else if (field === 'importeUSD') {
            updated = syncDolaresAmounts(updated, field, updated.importe || 0, rate, roundToPeso(value || 0));
          } else if (field === 'importe') {
            updated = syncDolaresAmounts(updated, field, roundToPeso(value || 0), rate);
          } else if (field === 'metodoPago' && value === 'dolares') {
            updated = syncDolaresAmounts(updated, field, updated.importe || 0, rate);
          }
        }

        return updated;
      })
    );
  };

  const calculateRemainingAmount = () => {
    if (!selectedFactura) return 0;
    const totalPaid = paymentMethods.reduce((sum, pm) => sum + (pm.importe || 0), 0);
    // Account for saldo aplicado
    const effectiveTotal = selectedFactura.total - saldoAplicado;
    return effectiveTotal - totalPaid;
  };

  const handleConfirmar = async () => {
    // console.log("DEBUG: handleConfirmar started", { selectedFactura, enviarACuentaCorriente, paymentMethods });
    if (!selectedFactura || !selectedFactura.id) return;

    const methodsForSubmit = paymentMethods.map(normalizeDolaresPaymentForSubmit);

    const ccOk = confirmarSaldoHabilitado && enviarACuentaCorriente;
    const restoOk = confirmarSaldoHabilitado && !ccOk && acreditarResto;

    if (!ccOk) {
      // If paying in full with saldo, allow empty payment methods
      const effectiveTotal = selectedFactura.total - saldoAplicado;
      if (effectiveTotal > 0) {
        const invalidMethods = methodsForSubmit.filter(pm => !pm.metodoPago || !pm.importe || pm.importe <= 0);
        if (invalidMethods.length > 0) {
          toast.error("Complete todos los métodos de pago");
          return;
        }
        const totalPaid = methodsForSubmit.reduce((sum, pm) => sum + roundToPeso(pm.importe), 0);
        const hasUsd = methodsForSubmit.some(
          (pm) => String(pm.metodoPago).toLowerCase() === 'dolares',
        );
        if (restoOk) {
          if (totalPaid + 0.01 < effectiveTotal) {
            toast.error(
              `El total pagado ($${totalPaid.toLocaleString('es-AR')}) no cubre el monto a pagar ($${effectiveTotal.toLocaleString('es-AR')})`,
            );
            return;
          }
        } else if (Math.abs(totalPaid - effectiveTotal) > 0.01) {
          // F5: USD de más + OFF → vuelto en caja (haber ARS); no exigir acreditar sobrante.
          const overpayUsd = hasUsd && totalPaid > effectiveTotal + 0.01;
          if (!overpayUsd) {
            toast.error(
              `El total pagado ($${totalPaid.toLocaleString('es-AR')}) debe coincidir con el monto a pagar ($${effectiveTotal.toLocaleString('es-AR')}). Active «Acreditar sobrante» si paga de más.`,
            );
            return;
          }
        }
        for (const pm of methodsForSubmit) {
          if (String(pm.metodoPago).toLowerCase() === 'cheque' && pm.importe > 0) {
            const n = String(pm.chequeNumero || '').trim();
            if (!n) {
              toast.error('Ingrese el número de cheque');
              return;
            }
          }
          if (metodoRequiereReferencia(pm.metodoPago) && pm.importe > 0) {
            if (!String(pm.referencia || '').trim()) {
              toast.error('Ingrese la referencia (comprobante/autorización) para transferencia o tarjeta');
              return;
            }
          }
          if (String(pm.metodoPago).toLowerCase() === 'dolares' && pm.importe > 0) {
            if (!pm.importeUSD || pm.importeUSD <= 0) {
              toast.error('Ingrese el importe en USD');
              return;
            }
            if (!pm.tasaCambio || pm.tasaCambio <= 0) {
              toast.error('Ingrese la cotización USD/ARS');
              return;
            }
            if (!isDolaresAmountsConsistent(pm)) {
              toast.error('El importe en pesos no coincide con USD × cotización');
              return;
            }
          }
        }
      }
    }

    const paymentData = {
      enviarACuentaCorriente: ccOk,
      acreditarResto: restoOk,
      saldoAplicado: saldoAplicado,  // Amount of client credit applied
      metodosPago: ccOk
        ? [{ metodoPago: 'cuenta corriente', importe: selectedFactura.total }]
        : methodsForSubmit.map(pm => ({
          metodoPago: pm.metodoPago,
          metodoTipo: pm.metodoTipo || undefined,
          importe: roundToPeso(pm.importe),
          importeUSD: pm.importeUSD != null ? roundToPeso(pm.importeUSD) : undefined,
          tasaCambio: pm.tasaCambio != null ? roundMoney(pm.tasaCambio) : undefined,
          referencia: pm.referencia ? String(pm.referencia).trim() : undefined,
          chequeNumero: pm.chequeNumero,
          chequeBanco: pm.chequeBanco,
          chequeFecha: pm.chequeFecha,
          chequeFechaCreacion: pm.chequeFechaCreacion,
          chequeTipo: pm.chequeTipo,
        }))
    };

    setConfirmingFactura(selectedFactura.numero);

    try {
      const sucursalId =
        Number(selectedFactura.sucursalId ?? selectedFactura.sucursal_id) || null;
      const fechaSolicitada = String(selectedFactura.fecha || "").slice(0, 10) || today;
      let aplicada = nextFechaVentaYmd(_cierresCache, sucursalId, fechaSolicitada);
      try {
        const st = await ventasAPI.getFechaVenta({
          fecha: fechaSolicitada,
          ...(selectedFactura.puntoVentaId
            ? { punto_venta: selectedFactura.puntoVentaId }
            : {}),
          ...(sucursalId ? { sucursal: sucursalId } : {}),
        });
        if (st?.fecha) aplicada = st.fecha;
      } catch {
        /* skip-forward local si GET falla */
      }
      if (ventaRequiereAvisoFecha(fechaSolicitada, aplicada)) {
        setConfirmingFactura(null);
        setPendingConfirmPayload({
          paymentData,
          tipo: confirmType,
          facturaTipo: selectedFacturaTipo,
          facturaId: selectedFactura.id,
        });
        setFechaAvisoYmd(aplicada);
        setFechaModalOpen(true);
        return;
      }

      await procederConfirmacionTrasFecha({
        paymentData,
        tipo: confirmType,
        facturaTipo: selectedFacturaTipo,
        facturaId: selectedFactura.id,
      });
    } catch (error) {
      console.error("Error confirmando factura:", error);
    } finally {
      setConfirmingFactura(null);
    }
  };

  const resetPaymentDialogState = () => {
    setShowPaymentDialog(false);
    setPaymentMethods([]);
    setEnviarACuentaCorriente(false);
    setAcreditarResto(false);
    setGenerarRemitoAuto(false);
  };

  const downloadRemitoPdfBlob = async (numero: string) => {
    try {
      const file = await ventaRemitosAPI.downloadRemitoPDF(numero);
      const url = window.URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading remito PDF:", err);
      toast.error("Factura OK; no se pudo descargar el PDF del remito");
    }
  };

  const openAsignacionDeposito = async (pending: {
    paymentData: any;
    tipo: "cae" | "negro";
    facturaTipo: "A" | "B" | "X";
    facturaId: number;
  }) => {
    setConfirmingFactura(selectedFactura?.numero ?? String(pending.facturaId));
    try {
      const opciones = await ventasAPI.previewEntregaDeposito(pending.facturaId);
      const plan = buildAsignacionPlan(opciones, null);
      setAsignacionOpciones(opciones);
      setAsignacionSelectedDep(plan.selectedDepositoId);
      setPendingAutoRemito(pending);
      setShowPaymentDialog(false);
      setShowAsignacionModal(true);
    } catch (error: unknown) {
      console.error("Error preview entrega depósito:", error);
      const err = error as { message?: string; details?: { message?: string } };
      toast.error(
        err?.details?.message ||
          err?.message ||
          "No se pudo consultar el stock para la entrega automática",
      );
    } finally {
      setConfirmingFactura(null);
    }
  };

  const cancelAsignacionDeposito = () => {
    if (asignacionSaving) return;
    setShowAsignacionModal(false);
    setAsignacionOpciones(null);
    setAsignacionSelectedDep(null);
    setPendingAutoRemito(null);
    setShowPaymentDialog(true);
  };

  const procederConfirmacionTrasFecha = async (pending: {
    paymentData: any;
    tipo: "cae" | "negro";
    facturaTipo: "A" | "B" | "X";
    facturaId: number;
  }) => {
    if (generarRemitoAuto && !clienteEsDeudor(clienteSaldo)) {
      await openAsignacionDeposito(pending);
      return;
    }
    await emitirFacturaYCerrar(pending);
  };

  const emitirFacturaYCerrar = async (pending: {
    paymentData: any;
    tipo: "cae" | "negro";
    facturaTipo: "A" | "B" | "X";
    facturaId: number;
  }) => {
    const factura = selectedFactura;
    if (!factura || factura.id !== pending.facturaId) return;
    setConfirmingFactura(factura.numero);
    try {
      await onConfirmarFactura(pending.facturaId, pending.tipo, pending.paymentData, pending.facturaTipo);
      toast.success("Factura confirmada exitosamente");
      dataRefresh.emit("clientes");
      await handleDownloadPDF(factura.id, factura.numero, factura.tipoComprobante);
      resetPaymentDialogState();
    } catch (error) {
      console.error("Error confirmando factura:", error);
    } finally {
      setConfirmingFactura(null);
    }
  };

  const confirmarAsignacionYEmitir = async () => {
    const pending = pendingAutoRemito;
    const factura = selectedFactura;
    const opciones = asignacionOpciones;
    if (!pending || !factura || factura.id !== pending.facturaId || !opciones) return;
    const plan = buildAsignacionPlan(opciones, asignacionSelectedDep);
    if (!canConfirmAsignacion(plan)) return;

    setAsignacionSaving(true);
    setConfirmingFactura(factura.numero);
    let facturaEmitida = false;
    try {
      // 1) Emitir factura (crea remito pendiente; no descuenta stock).
      await onConfirmarFactura(pending.facturaId, pending.tipo, pending.paymentData, pending.facturaTipo);
      facturaEmitida = true;
      dataRefresh.emit("clientes");
      dataRefresh.emit("remitos");
      dataRefresh.emit("stock");

      // 2) Remito auto-creado → finalizar entregado con la asignación elegida.
      const remitos = await ventaRemitosAPI.list({ facturaId: pending.facturaId });
      const remito =
        remitos.find((r) => r.estado === "pendiente" || r.estado === "parcial") || remitos[0];
      if (!remito?.numero) {
        toast.error(
          "Factura emitida, pero no se encontró el remito para finalizar. Finalícelo desde Remitos Vtas.",
        );
        await handleDownloadPDF(factura.id, factura.numero, factura.tipoComprobante);
        setShowAsignacionModal(false);
        setAsignacionOpciones(null);
        setPendingAutoRemito(null);
        resetPaymentDialogState();
        return;
      }
      const entregaItems = mapAsignacionToRemitoEntrega(remito, plan, factura.items || []);
      if (entregaItems.length === 0) {
        toast.error(
          "Factura emitida; no se pudo mapear la asignación al remito. Finalícelo desde Remitos Vtas.",
        );
        await handleDownloadPDF(factura.id, factura.numero, factura.tipoComprobante);
        setShowAsignacionModal(false);
        setAsignacionOpciones(null);
        setPendingAutoRemito(null);
        resetPaymentDialogState();
        return;
      }
      await ventaRemitosAPI.marcarEntregado(String(remito.numero), entregaItems);
      dataRefresh.emit("stock");
      dataRefresh.emit("remitos");
      toast.success("Factura emitida y remito finalizado (entregado)");

      await handleDownloadPDF(factura.id, factura.numero, factura.tipoComprobante);
      await downloadRemitoPdfBlob(String(remito.numero));

      setShowAsignacionModal(false);
      setAsignacionOpciones(null);
      setPendingAutoRemito(null);
      resetPaymentDialogState();
    } catch (error: unknown) {
      console.error("Error en atajo factura+remito:", error);
      const err = error as { message?: string; details?: { message?: string }; status?: number };
      if (facturaEmitida) {
        // No re-emitir CAE/FX: la factura ya salió; remito/stock quedan para Remitos Vtas.
        toast.error(
          err?.details?.message ||
            err?.message ||
            "Factura emitida; no se pudo finalizar el remito. Complételo en Remitos Vtas (sin reemitir).",
        );
        try {
          await handleDownloadPDF(factura.id, factura.numero, factura.tipoComprobante);
        } catch {
          /* ignore */
        }
        setShowAsignacionModal(false);
        setAsignacionOpciones(null);
        setPendingAutoRemito(null);
        resetPaymentDialogState();
      } else {
        toast.error(
          err?.details?.message ||
            err?.message ||
            "No se pudo emitir la factura. Puede reintentar o desactivar el atajo.",
        );
        setShowAsignacionModal(false);
        setAsignacionOpciones(null);
        setPendingAutoRemito(null);
        setShowPaymentDialog(true);
      }
    } finally {
      setAsignacionSaving(false);
      setConfirmingFactura(null);
    }
  };

  const confirmarFechaVentaYEmitir = async () => {
    const pending = pendingConfirmPayload;
    setFechaModalOpen(false);
    setPendingConfirmPayload(null);
    setFechaAvisoYmd("");
    if (!pending || !selectedFactura || selectedFactura.id !== pending.facturaId) return;
    try {
      await procederConfirmacionTrasFecha(pending);
    } catch (error) {
      console.error("Error confirmando factura:", error);
    }
  };

  // Date-only API values: never `new Date("YYYY-MM-DD")` (UTC −1 día en AR).
  const formatDate = (dateString: string) => formatDateOnlyEsAR(dateString);
  const sortedRows = useMemo(
    () => sortFacturaRows(rows, { key: sortKey, dir: sortDir }),
    [rows, sortKey, sortDir],
  );

  const renderSortHeader = (label: string, key: FacturaSortKey, align: "left" | "right" = "left") => {
    const active = sortKey === key;
    const Icon = active && sortDir === "desc" ? ArrowDown : ArrowUp;
    return (
      <th
        className={`py-3 px-4 font-medium text-sm ${align === "right" ? "text-right" : "text-left"}`}
        aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
      >
        <button
          type="button"
          onClick={() => {
            const next = toggleFacturaSort({ key: sortKey, dir: sortDir }, key);
            setSortKey(next.key);
            setSortDir(next.dir);
          }}
          className={`inline-flex items-center gap-1 font-medium hover:text-foreground ${align === "right" ? "w-full justify-end" : ""}`}
          title={active ? (sortDir === "asc" ? "Orden ascendente" : "Orden descendente") : `Ordenar por ${label}`}
        >
          {label}
          <Icon className={`h-3.5 w-3.5 ${active ? "" : "opacity-40"}`} aria-hidden />
        </button>
      </th>
    );
  };

  const handleDeleteFactura = () => {
    if (!facturaToDelete || !facturaToDelete.id) return;
    onEliminarFactura(facturaToDelete.id);
    toast.success(`Factura ${facturaToDelete.numero} eliminada`);
    setFacturaToDelete(null);
  };

  const remainingAmount = calculateRemainingAmount();
  const colCount = seeTotal ? 9 : 8;
  const clienteDeudor = clienteEsDeudor(clienteSaldo);
  const asignacionPlan: AsignacionPlan | null = asignacionOpciones
    ? buildAsignacionPlan(asignacionOpciones, asignacionSelectedDep)
    : null;
  const asignacionConfirmOk = asignacionPlan ? canConfirmAsignacion(asignacionPlan) : false;

  return (
    <>
      <div className="mb-6 flex flex-col gap-3 overflow-visible lg:flex-row lg:items-center">
        <div className="min-w-0 flex-1 max-w-md">
          <SearchInput
            placeholder="Buscar (todas las palabras: cliente, número, CUIT…)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
          {isHistorial ? (
            <>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Mes</span>
                <MonthInput
                  id="historial-mes"
                  value={historialMonth}
                  max={previousMonthValue(today)}
                  onChange={(next) => {
                    if (!next || next >= today.slice(0, 7)) return;
                    setHistorialMonth(next);
                  }}
                />
              </label>
              <select
                className="h-8 max-w-[12rem] rounded-md border border-input bg-background px-2 text-sm"
                value={sucursalId}
                onChange={(e) => setSucursalId(e.target.value)}
              >
                <option value="">Todas las sucursales</option>
                {sucursales.map((s) => (
                  <option key={s.id ?? s.codigo} value={String(s.id ?? "")}>
                    {s.nombre}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <>
              {elevated ? (
                <select
                  className="h-8 max-w-[12rem] rounded-md border border-input bg-background px-2 text-sm"
                  value={sucursalId}
                  onChange={(e) => setSucursalId(e.target.value)}
                  title="Sucursal del carrusel"
                >
                  {sucursales.map((s) => (
                    <option key={s.id ?? s.codigo} value={String(s.id ?? "")}>
                      {s.nombre}
                    </option>
                  ))}
                </select>
              ) : null}
              <CierreDiasCarousel
                days={chipDays}
                today={today}
                selectedYmd={selectedFecha}
                onSelect={handleChipSelect}
                cierres={cierreDias}
                sucursalId={effectiveSucursalId}
              />
            </>
          )}
        </div>
      </div>

      <div className="relative overflow-x-auto">
        {listLoading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : null}
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              {renderSortHeader("Nro Factura", "numero")}
              {renderSortHeader("Presupuesto", "presupuesto")}
              {renderSortHeader("Cliente", "cliente")}
              {renderSortHeader("CUIT/DNI", "documento")}
              {renderSortHeader("Fecha", "fecha")}
              {seeTotal ? renderSortHeader("Total", "total", "right") : null}
              {renderSortHeader("Estado", "estado")}
              <th className="text-center py-3 px-4 font-medium text-sm">Ver</th>
              <th className="text-center py-3 px-4 font-medium text-sm">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="py-8 text-center text-muted-foreground">
                  {searchQuery.trim() ? "No se encontraron resultados" : "No hay facturas para mostrar"}
                </td>
              </tr>
            ) : null}
            {sortedRows.map((factura, index) => {
              const cliente = factura.cliente as any;
              const tiposCliente = normalizeClienteTiposPermitidos(cliente?.tipoFacturaPermitida);
              const showDni = factura.tipoFactura === "B" ||
                (!factura.tipoFactura && tiposCliente.includes("B") && !tiposCliente.includes("A"));
              const idValue = showDni ? (cliente?.dni || cliente?.cuit) : cliente?.cuit;
              const idLabel = showDni ? "DNI" : "CUIT";

              if (index === 0) {
                // console.log("[DEBUG FACTURA]", factura.numero, "Estado:", factura.estado, "MetodoPago:", factura.metodoPago, "Metodo_Pago:", (factura as any).metodo_pago);
              }

              const facturaKey = factura.id != null
                ? String(factura.id)
                : [
                    factura.tipoComprobante || factura.tipoFactura || 'sin-tipo',
                    factura.numero,
                    factura.fecha,
                    factura.presupuestoId ?? 'sin-presupuesto',
                  ].join(':');

              return (
                <tr key={facturaKey} className="border-b border-border hover:bg-muted/50">
                  <td className="py-3 px-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-mono">{factura.numero}</span>
                      {(factura.tipoComprobante || '').toUpperCase().startsWith('NC-') ? (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 min-w-[20px] justify-center">
                          {factura.tipoComprobante}
                        </Badge>
                      ) : factura.tipoFactura ? (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 min-w-[20px] justify-center">
                          {factura.tipoFactura}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1">
                      <RemitoOperatorBadges source={factura} showGeneroLabel={false} />
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm">{factura.nroPresupuesto || factura.presupuestoId || '-'}</td>
                  <td className="py-3 px-4 text-sm">
                    {cliente?.codigo === "CF" ? "C.Final" : cliente?.nombre}
                  </td>
                  <td className="py-3 px-4 text-sm">
                    <div className="flex flex-col">
                      <span>{idValue || '-'}</span>
                      <span className="text-[10px] text-muted-foreground">{idLabel}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm">
                    {formatDate(factura.fecha)}
                  </td>
                  {seeTotal ? (
                    <td className="py-3 px-4 text-sm text-right">
                      ${Number(factura.total ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </td>
                  ) : null}
                  <td className="py-3 px-4 text-sm">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className={`px-2 py-1 rounded-full text-xs ${(() => {
                        const isNC = (factura.tipoComprobante || '').toUpperCase().startsWith('NC-');
                        const estado = factura.estado?.toLowerCase() || '';
                        const fiscal = factura.estadoFiscal;
                        if (fiscal === 'INCIERTO' || fiscal === 'ENVIANDO') {
                          return 'bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100';
                        }
                        if (fiscal === 'RECHAZADO') {
                          return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
                        }
                        if (isNC) return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
                        if (estado === 'pendiente') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
                        if (estado.includes('cae') || estado.includes('afip')) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
                        if (estado.includes('negro')) return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
                        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
                      })()}`}>
                        {facturaEstadoLabel(factura)}
                      </span>
                      {((factura.metodoPago || (factura as any).metodo_pago)?.toLowerCase().includes('cuenta corriente') || factura.estado?.toLowerCase().includes('cc')) && (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                          CC
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleViewProducts(factura)}
                      className="h-8 w-8 p-0"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </td>
                  <td className="py-3 px-4">
                    {(() => {
                      const isNC = (factura.tipoComprobante || '').toUpperCase().startsWith('NC-');
                      const tieneNC =
                        factura.tieneNotaCredito === true
                        || (factura.id != null && ncEmitidasIds.has(factura.id));
                      const fiscal = (factura.estadoFiscal || '').toUpperCase();
                      const estadoLower = factura.estado?.toLowerCase() || '';
                      const isRechazadaArca =
                        !isNC && (fiscal === 'RECHAZADO' || estadoLower.includes('rechazado'));
                      const isConfirmada =
                        !isRechazadaArca && (
                          fiscal === 'AUTORIZADO'
                          || fiscal === 'LOCAL'
                          || estadoLower.includes('confirmado')
                          || (estadoLower.includes('cae') && fiscal !== 'RECHAZADO')
                          || estadoLower.includes('negro')
                        );
                      const canFinalize = !isReadOnly && !isNC && (
                        estadoLower === 'pendiente' || isRechazadaArca
                      );
                      const tipoCbte = (factura.tipoComprobante || "").toUpperCase();
                      const isFxRow = tipoCbte === "X" || estadoLower.includes("negro");
                      const isCaeRow = !isNC && !isFxRow && (
                        fiscal === "AUTORIZADO"
                        || estadoLower.includes("cae")
                        || estadoLower.includes("afip")
                      );
                      const tieneNcX =
                        factura.tieneNcX === true
                        || factura.tiene_nc_x === true
                        || (factura.id != null && ncXEmitidasIds.has(factura.id));
                      const showNc = !isReadOnly && !isNC && !!onGenerarNC && isConfirmada;
                      const ncDisabled = tieneNC || (isCaeRow && tieneNcX);
                      const ncXDisabled = tieneNcX || tieneNC;
                      const showNcX = !isReadOnly && elevated && isCaeRow && !!onGenerarNCX;
                      const openNcDialog = (interna: boolean) => {
                        void hydrateFactura(factura).then((full) => {
                          setNcInterna(interna);
                          setFacturaForNC(full);
                          setNcEsCreditoCliente(false);
                          setNcIsPartial(false);
                          setNcSelectedItems(buildNcItemMap(full.items as any[], { parcial: false }));
                        });
                      };
                      const needsReconcile = !isReadOnly && (
                        factura.estadoFiscal === 'INCIERTO'
                        || factura.estadoFiscal === 'ENVIANDO'
                      ) && !!onReconciliarFactura && !!factura.id;

                      if (needsReconcile) {
                        return (
                          <div className="flex gap-2 justify-center">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs border-amber-600 text-amber-800 hover:bg-amber-50"
                              disabled={reconcilingFactura === factura.id}
                              onClick={async () => {
                                if (!factura.id || !onReconciliarFactura) return;
                                setReconcilingFactura(factura.id);
                                try {
                                  await onReconciliarFactura(factura.id);
                                } finally {
                                  setReconcilingFactura(null);
                                }
                              }}
                              title="Consultar ARCA (FECompConsultar) sin reenviar"
                            >
                              {reconcilingFactura === factura.id ? 'Consultando…' : 'Reconciliar'}
                            </Button>
                          </div>
                        );
                      }

                      if (isNC) {
                        return (
                          <div className="flex gap-2 justify-center">
                            <Button size="sm" variant="ghost" onClick={() => handleDownloadPDF(factura.id, factura.numero, factura.tipoComprobante)} title="Descargar PDF">
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        );
                      }
                      if (canFinalize) {
                        const tiposC = normalizeClienteTiposPermitidos((factura.cliente as any)?.tipoFacturaPermitida);
                        const permiteCae = clientePermiteCae(tiposC);
                        return (
                          <div className="flex gap-2 justify-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenPaymentDialog(factura, 'cae')}
                              disabled={confirmingFactura === factura.numero || !permiteCae}
                              title={
                                isRechazadaArca
                                  ? 'Reprocesar CAE con el mismo número (corrige el dato del cliente si ARCA rechazó)'
                                  : !permiteCae
                                    ? 'El cliente no tiene Factura A ni B habilitadas (solo X no admite CAE)'
                                    : undefined
                              }
                              className={facturaCaeBtnClass}
                            >
                              CAE
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenPaymentDialog(factura, 'negro')}
                              disabled={confirmingFactura === factura.numero}
                              title={
                                isRechazadaArca
                                  ? 'Cerrar como FX y liberar el número si ARCA no lo autorizó'
                                  : undefined
                              }
                              className={facturaFinalizeBtnBox}
                            >
                              FX
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setFacturaToDelete(factura)}
                              className={`h-8 w-8 p-0 ${deleteIconButtonClass}`}
                              title="Borrar"
                              aria-label="Borrar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        );
                      }
                      if (!isConfirmada) {
                        return <div className="flex gap-2 justify-center" />;
                      }
                      return (
                        <div className="flex gap-2 justify-center">
                          <Button size="sm" variant="ghost" onClick={() => handleDownloadPDF(factura.id, factura.numero, factura.tipoComprobante)}>
                            <Download className="h-4 w-4" />
                          </Button>
                          {showNc && (
                            <span title={ncDisabled ? "Ya tiene NC" : undefined}>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={ncDisabled}
                                onClick={() => openNcDialog(false)}
                                className={
                                  ncDisabled
                                    ? "text-xs text-muted-foreground border-muted"
                                    : "text-xs text-orange-600 border-orange-600 hover:bg-orange-50"
                                }
                                title={ncDisabled ? "Ya tiene NC" : "Generar Nota de Crédito (una sola por factura)"}
                              >
                                NC
                              </Button>
                            </span>
                          )}
                          {showNcX && (
                            <span title={ncXDisabled ? "Ya tiene NC" : undefined}>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={ncXDisabled}
                                onClick={() => openNcDialog(true)}
                                className={
                                  ncXDisabled
                                    ? "text-xs text-muted-foreground border-muted"
                                    : "text-xs text-orange-600 border-orange-600 hover:bg-orange-50"
                                }
                                title={
                                  ncXDisabled
                                    ? "Ya tiene NC"
                                    : "NC-X interna: no anula el CAE y no llama a AFIP"
                                }
                              >
                                NC-X
                              </Button>
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={page}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        pageSizeOptions={FACTURA_PAGE_SIZE_OPTIONS}
      />
      {totalCount === 0 ? (
        <div className="flex items-center gap-2 pb-4 text-sm text-muted-foreground">
          <span>Mostrar:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="px-2 py-1 border border-input rounded bg-background text-foreground text-sm"
          >
            {FACTURA_PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      ) : null}

      {/* Products Detail Dialog */}
      <Dialog open={showProductsDialog} onOpenChange={setShowProductsDialog}>
        <DialogContent className="w-[calc(100%-2rem)] sm:max-w-[96vw] max-h-[90vh] min-w-0 overflow-x-hidden overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap pr-8">
              <span>Detalle - Factura {viewingFactura?.numero}</span>
              {viewingFactura ? (
                <RemitoOperatorBadges source={viewingFactura} showGeneroLabel={false} />
              ) : null}
            </DialogTitle>
          </DialogHeader>

          {viewingFactura && (
            <div className="space-y-4 min-w-0">
              <div className="min-w-0">
                <h4 className="font-semibold text-sm mb-2">Productos</h4>
                {/* Scroll vertical ~7 filas. Overflow-x en la grilla (no table-fixed) si no entra; el diálogo no scrollea en horizontal. */}
                <div className="border border-input rounded-lg overflow-x-auto overflow-y-auto max-h-[calc(2.5rem+7*2.25rem)] min-w-0">
                  <table className="w-full min-w-[640px]">
                    <thead className="bg-muted sticky top-0 z-10">
                      <tr>
                        <th className="text-left py-2 px-3 text-sm font-medium min-w-[16rem]">Producto</th>
                        <th className="text-center py-2 px-2 text-sm font-medium whitespace-nowrap">Cantidad</th>
                        <th className="text-right py-2 px-2 text-sm font-medium whitespace-nowrap">Precio Unit.</th>
                        <th className="text-center py-2 px-2 text-sm font-medium">Desc.</th>
                        <th className="text-center py-2 px-2 text-sm font-medium">Desc.</th>
                        <th className="text-right py-2 px-2 text-sm font-medium whitespace-nowrap">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupComboVisualRows(viewingFactura.items || []).map((row) => {
                        const item = row.legs[0] as any;
                        const nombre = row.isCombo
                          ? comboVisualNombrePdf(row.legs)
                          : facturaItemNombre(item);
                        const cantidad = item.cantidad || 0;
                        const precio = row.isCombo
                          ? comboVisualPrecioSum(row.legs)
                          : parseFloat(item.precio_unitario || item.precioUnitario || 0);
                        const desc1 = parseFloat(item.descuento1 || 0);
                        const desc2 = parseFloat(item.descuento2 || 0);
                        const subtotalFinal = row.legs.reduce((s, raw) => {
                          const it = raw as any;
                          const p = parseFloat(it.precio_unitario || it.precioUnitario || 0);
                          const q = it.cantidad || 0;
                          const d1 = parseFloat(it.descuento1 || 0);
                          const d2 = parseFloat(it.descuento2 || 0);
                          let sub = p * q;
                          if (d1 > 0) sub -= sub * d1 / 100;
                          if (d2 > 0) sub -= sub * d2 / 100;
                          return s + sub;
                        }, 0);
                        const itemId = comboVisualPersistedSaveId(row);
                        const isSaving = itemId != null && savingNombreItemId === itemId;
                        return (
                          <tr key={row.key} className="border-t border-input">
                            <td className="py-2 px-3 text-sm align-top whitespace-nowrap">
                              {canEditItemNombre && itemId != null ? (
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={editNombres[itemId] ?? nombre}
                                    onChange={(e) =>
                                      setEditNombres((prev) => ({
                                        ...prev,
                                        [itemId]: e.target.value,
                                      }))
                                    }
                                    onBlur={() => void handleSaveItemNombre(itemId)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        (e.target as HTMLInputElement).blur();
                                      }
                                    }}
                                    disabled={isSaving}
                                    className="h-8 text-sm w-full min-w-[16rem]"
                                    aria-label={row.isCombo ? "Nombre del combo en PDF" : "Nombre del artículo"}
                                  />
                                  {isSaving && (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />
                                  )}
                                </div>
                              ) : (
                                <span className="block max-w-[22rem] truncate" title={nombre}>{nombre}</span>
                              )}
                            </td>
                            <td className="py-2 px-2 text-sm text-center whitespace-nowrap">{cantidad}</td>
                            <td className="py-2 px-2 text-sm text-right whitespace-nowrap">${Number(precio).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                            <td className="py-2 px-2 text-sm text-center whitespace-nowrap">
                              {desc1 > 0 ? `${desc1}%` : '-'}
                            </td>
                            <td className="py-2 px-2 text-sm text-center whitespace-nowrap">
                              {desc2 > 0 ? `${desc2}%` : '-'}
                            </td>
                            <td className="py-2 px-2 text-sm text-right whitespace-nowrap">${subtotalFinal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-muted border-t-2 border-input">
                      <tr>
                        <td colSpan={5} className="py-2 px-4 text-sm font-semibold text-right">Total:</td>
                        <td className="py-2 px-4 text-sm text-right font-bold">
                          ${Number(viewingFactura.total).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowProductsDialog(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full-screen lock overlay when confirming CAE/FX */}
      {confirmingFactura && (
        <LoadingOverlay
          message={confirmType === "cae" ? "Obteniendo CAE de AFIP..." : "Procesando..."}
          submessage="Por favor espere, no cierre esta ventana"
        />
      )}

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={(open) => !open && !confirmingFactura && setShowPaymentDialog(false)}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto gap-2">
          <DialogHeader className="gap-1.5">
            <DialogTitle>
              {enviarACuentaCorriente ? "Confirmar Factura" : "Métodos de Pago"}
            </DialogTitle>
            <DialogDescription>
              {enviarACuentaCorriente ? (
                <>
                  {`Factura ${selectedFactura?.numero} será enviada a Cuenta Corriente`}
                  <br />
                </>
              ) : null}
              <span className="font-semibold">Total: ${Number(selectedFactura?.total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-1 pb-4">
            {confirmType === "cae" && selectedFactura && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
                <h4 className="font-semibold text-sm mb-2 text-blue-800 dark:text-blue-300">Tipo de Factura</h4>
                <div className="flex gap-4">
                  {normalizeClienteTiposPermitidos((selectedFactura.cliente as any)?.tipoFacturaPermitida).includes("A") && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="facturaTipo"
                        value="A"
                        checked={selectedFacturaTipo === "A"}
                        onChange={() => setSelectedFacturaTipo("A")}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm font-medium">Factura A</span>
                    </label>
                  )}
                  {normalizeClienteTiposPermitidos((selectedFactura.cliente as any)?.tipoFacturaPermitida).includes("B") && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="facturaTipo"
                        value="B"
                        checked={selectedFacturaTipo === "B"}
                        onChange={() => setSelectedFacturaTipo("B")}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm font-medium">Factura B</span>
                    </label>
                  )}
                </div>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  {selectedFacturaTipo === "A"
                    ? "Para Responsables Inscriptos (con IVA discriminado)"
                    : "Para Consumidores Finales / Exentos"}
                </p>
              </div>
            )}

            {confirmType === "negro" && (
              <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <span className="text-sm font-medium">Tipo de Comprobante:</span>
                <Badge variant="outline" className="bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border-0">
                  Factura X (Presupuesto)
                </Badge>
              </div>
            )}
            {/* Client Saldo Section - Only show if client has positive balance (a favor) */}
            {clienteSaldo > 0 && (
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="aplicar-saldo-favor" className="text-sm font-medium text-green-800 dark:text-green-300 cursor-pointer">
                        💰 Saldo a Favor del Cliente
                      </Label>
                      <Badge className="bg-green-600 text-white hover:bg-green-700">
                        ${clienteSaldo.toLocaleString('es-AR', { minimumFractionDigits: 2 })} (a favor)
                      </Badge>
                    </div>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      {aplicarSaldo
                        ? "El saldo será aplicado para reducir el monto a pagar"
                        : "Active para aplicar el saldo a favor"}
                    </p>

                    {aplicarSaldo && (
                      <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-700 space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-green-700 dark:text-green-400">Total factura:</span>
                          <span className="font-medium">${(Number(selectedFactura?.total) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-green-700 dark:text-green-400">Saldo aplicado:</span>
                          <span className="font-medium text-green-600">- ${saldoAplicado.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between text-sm font-bold pt-1 border-t border-green-200 dark:border-green-700">
                          <span className="text-green-800 dark:text-green-300">Monto a pagar:</span>
                          <span className="text-green-800 dark:text-green-200">
                            ${((Number(selectedFactura?.total) || 0) - saldoAplicado).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <Switch
                    id="aplicar-saldo-favor"
                    checked={aplicarSaldo}
                    onCheckedChange={(checked) => {
                      setAplicarSaldo(checked);
                      const facturaTotal = Number(selectedFactura?.total) || 0;
                      const newEffectiveTotal = checked
                        ? facturaTotal - Math.min(clienteSaldo, facturaTotal)
                        : facturaTotal;
                      if (paymentMethods.length > 0) {
                        setPaymentMethods((prev) => {
                          const pm = prev[0];
                          let updated: PaymentMethod = {
                            ...pm,
                            importe: newEffectiveTotal > 0 ? newEffectiveTotal : 0,
                          };
                          if (updated.metodoPago === 'dolares' && (updated.tasaCambio || 0) > 0) {
                            updated = syncDolaresAmounts(
                              updated,
                              'importe',
                              updated.importe || 0,
                              roundMoney(updated.tasaCambio)
                            );
                          }
                          return [updated, ...prev.slice(1)];
                        });
                      }
                    }}
                    className="mt-0.5 data-[state=checked]:bg-green-600"
                  />
                </div>
              </div>
            )}

            <div className={`p-4 rounded-lg border transition-colors ${enviarACuentaCorriente ? 'bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-800' : 'bg-amber-50 border-amber-100 dark:bg-amber-900/20 dark:border-amber-800'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <Label
                      htmlFor="enviar-cuenta-corriente"
                      className={`text-sm font-medium cursor-pointer ${enviarACuentaCorriente ? 'text-purple-800 dark:text-purple-300' : 'text-amber-800 dark:text-amber-300'}`}
                    >
                      Enviar a Cuenta Corriente
                    </Label>
                    {enviarACuentaCorriente && (
                      <Badge className="bg-purple-600 text-white hover:bg-purple-700">CC</Badge>
                    )}
                  </div>
                  <p className={`text-xs mt-1 ${enviarACuentaCorriente ? 'text-purple-600 dark:text-purple-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {confirmarSaldoHabilitado
                      ? 'Envía el total de la factura a deuda del cliente (no es el sobrante de un cobro)'
                      : NC_SALDO_CF_COPY}
                  </p>
                </div>
                <Switch
                  id="enviar-cuenta-corriente"
                  checked={confirmarSaldoHabilitado && enviarACuentaCorriente}
                  disabled={!confirmarSaldoHabilitado}
                  onCheckedChange={(checked) => {
                    if (!confirmarSaldoHabilitado) return;
                    setEnviarACuentaCorriente(checked);
                    if (checked) setAcreditarResto(false);
                  }}
                  className="mt-0.5 data-[state=checked]:bg-purple-600"
                />
              </div>
            </div>

            <div
              className={`p-4 rounded-lg border transition-colors ${
                generarRemitoAuto
                  ? "bg-sky-50 border-sky-200 dark:bg-sky-900/20 dark:border-sky-800"
                  : "bg-slate-50 border-slate-200 dark:bg-slate-900/20 dark:border-slate-700"
              } ${clienteDeudor ? "opacity-70" : ""}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <Label
                      htmlFor="generar-remito-auto"
                      className={`text-sm font-medium ${
                        clienteDeudor ? "cursor-not-allowed" : "cursor-pointer"
                      } ${
                        generarRemitoAuto
                          ? "text-sky-800 dark:text-sky-300"
                          : "text-slate-800 dark:text-slate-300"
                      }`}
                    >
                      Emitir factura y entregar ahora (remito finalizado)
                    </Label>
                    {generarRemitoAuto && (
                      <Badge className="bg-sky-600 text-white hover:bg-sky-700">Remito</Badge>
                    )}
                  </div>
                  {clienteDeudor ? (
                    <p className="text-xs mt-1 text-amber-700 dark:text-amber-400">
                      Esta función se encuentra deshabilitada debido a que el cliente seleccionado es deudor, debe realizarse el cierre de remito de manera manual.
                    </p>
                  ) : generarRemitoAuto ? (
                    <p className="text-xs mt-1 text-sky-700 dark:text-sky-400">
                      Se va a emitir la factura, generar el remito, finalizarlo y descargar
                      ambos PDF. Vas a confirmar de dónde sale el stock (agrupado por
                      sucursal y depósito). El stock se descuenta una sola vez por línea.
                    </p>
                  ) : (
                    <p className="text-xs mt-1 text-slate-600 dark:text-slate-400">
                      Opcional: al confirmar, crea y finaliza el remito como entregado
                      (mismo camino que Remitos Vtas).
                    </p>
                  )}
                </div>
                <Switch
                  id="generar-remito-auto"
                  checked={generarRemitoAuto && !clienteDeudor}
                  disabled={clienteDeudor}
                  onCheckedChange={(checked) => {
                    if (clienteDeudor) return;
                    setGenerarRemitoAuto(checked);
                  }}
                  className="mt-0.5 data-[state=checked]:bg-sky-600"
                />
              </div>
            </div>

            {!enviarACuentaCorriente && (
              <>
                <div className={`p-4 rounded-lg border transition-colors ${acreditarResto ? 'bg-teal-50 border-teal-200 dark:bg-teal-900/20 dark:border-teal-800' : 'bg-slate-50 border-slate-200 dark:bg-slate-900/20 dark:border-slate-700'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <Label
                          htmlFor="acreditar-sobrante"
                          className={`text-sm font-medium cursor-pointer ${acreditarResto ? 'text-teal-800 dark:text-teal-300' : 'text-slate-800 dark:text-slate-300'}`}
                        >
                          Acreditar sobrante a saldo a favor
                        </Label>
                        {acreditarResto && (
                          <Badge className="bg-teal-600 text-white hover:bg-teal-700">A favor</Badge>
                        )}
                      </div>
                      <p className={`text-xs mt-1 ${acreditarResto ? 'text-teal-600 dark:text-teal-400' : 'text-slate-600 dark:text-slate-400'}`}>
                        {confirmarSaldoHabilitado
                          ? (acreditarResto
                            ? 'Si paga de más, el sobrante queda como crédito del cliente'
                            : 'Si paga de más en USD, el sobrante sale como vuelto (haber ARS) en caja; no deja saldo a favor')
                          : NC_SALDO_CF_COPY}
                      </p>
                    </div>
                    <Switch
                      id="acreditar-sobrante"
                      checked={confirmarSaldoHabilitado && acreditarResto}
                      disabled={!confirmarSaldoHabilitado}
                      onCheckedChange={(checked) => {
                        if (!confirmarSaldoHabilitado) return;
                        setAcreditarResto(checked);
                      }}
                      className="mt-0.5 data-[state=checked]:bg-teal-600"
                    />
                  </div>
                </div>

                {paymentMethods.map((pm, index) => (
                  <div key={pm.id} className="border border-input rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm">Método de Pago {index + 1}</h4>
                      {paymentMethods.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removePaymentMethod(pm.id)}
                          className={`h-6 w-6 p-0 ${deleteIconButtonClass}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor={`metodoPago-${pm.id}`}>Método</Label>
                        <Select
                          value={pm.metodoCodigo || pm.metodoPago}
                          onValueChange={(codigo) => {
                            const op = metodosPagoOpcionesRef.current.find((o) => o.codigo === codigo);
                            updatePaymentMethod(
                              pm.id,
                              'metodoPago',
                              op?.metodoPago ?? codigo,
                              codigo,
                              op?.tipo,
                            );
                          }}
                        >
                          <SelectTrigger id={`metodoPago-${pm.id}`}>
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            {metodosPagoOpciones.map((op) => (
                              <SelectItem key={op.codigo} value={op.codigo}>
                                {op.tipo}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor={`importe-${pm.id}`}>Importe</Label>
                        <CurrencyInput
                          id={`importe-${pm.id}`}
                          value={pm.importe}
                          onValueChange={(val) => updatePaymentMethod(pm.id, 'importe', val)}
                          placeholder="0.00"
                        />
                      </div>

                      {pm.metodoPago === "dolares" && (
                        <>
                          <div>
                            <Label htmlFor={`importeUSD-${pm.id}`}>Importe USD</Label>
                            <CurrencyInput
                              id={`importeUSD-${pm.id}`}
                              value={pm.importeUSD}
                              onValueChange={(val) => updatePaymentMethod(pm.id, 'importeUSD', val)}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`tasaCambio-${pm.id}`}>Tasa de Cambio (USD/ARS)</Label>
                            <NumberField
                              id={`tasaCambio-${pm.id}`}
                              value={pm.tasaCambio || ''}
                              onChange={(e) => updatePaymentMethod(pm.id, 'tasaCambio', parseFloat(e.target.value) || 0)}
                              placeholder="0.00"
                              step="0.01"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label>Equivalente en ARS</Label>
                            <div className="text-lg font-semibold text-green-600">
                              ${pm.importe ? pm.importe.toLocaleString('es-AR', { minimumFractionDigits: 2 }) : '0.00'}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {metodoRequiereReferencia(pm.metodoPago) && (
                      <div className="border-t border-input pt-3 mt-1">
                        <Label htmlFor={`referencia-${pm.id}`}>
                          Referencia / comprobante <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id={`referencia-${pm.id}`}
                          value={pm.referencia || ''}
                          onChange={(e) => updatePaymentMethod(pm.id, 'referencia', e.target.value)}
                          placeholder="Nº de transferencia, cupón o autorización"
                          maxLength={120}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Obligatorio para transferencia y tarjeta
                        </p>
                      </div>
                    )}

                    {pm.metodoPago === "cheque" && (
                      <div className="space-y-3 border-t border-input pt-3 mt-3">
                        <h5 className="font-semibold text-sm">Datos del Cheque</h5>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <Label htmlFor={`chequeTipo-${pm.id}`}>Tipo de Cheque</Label>
                            <Select
                              value={pm.chequeTipo || ''}
                              onValueChange={(value) => updatePaymentMethod(pm.id, 'chequeTipo', value)}
                            >
                              <SelectTrigger id={`chequeTipo-${pm.id}`}>
                                <SelectValue placeholder="Seleccionar tipo..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="fisico">Físico</SelectItem>
                                <SelectItem value="electronico">Electrónico</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor={`chequeNumero-${pm.id}`}>Número (máx 11 dígitos)</Label>
                            <Input
                              id={`chequeNumero-${pm.id}`}
                              value={pm.chequeNumero || ''}
                              onChange={(e) => updatePaymentMethod(pm.id, 'chequeNumero', e.target.value)}
                              placeholder="Ej: 12345678901"
                              maxLength={11}
                            />
                          </div>
                          <div>
                            <Label htmlFor={`chequeBanco-${pm.id}`}>Banco</Label>
                            <Input
                              id={`chequeBanco-${pm.id}`}
                              value={pm.chequeBanco || ''}
                              onChange={(e) => updatePaymentMethod(pm.id, 'chequeBanco', e.target.value)}
                              placeholder="Ej: Banco Galicia"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`chequeFechaCreacion-${pm.id}`}>Fecha de Creación</Label>
                            <DateInput
                              id={`chequeFechaCreacion-${pm.id}`}
                              value={pm.chequeFechaCreacion}
                              onChange={(value) => updatePaymentMethod(pm.id, 'chequeFechaCreacion', value)}
                              placeholder="dd/mm/yyyy"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`chequeFecha-${pm.id}`}>Fecha Diferido</Label>
                            <DateInput
                              id={`chequeFecha-${pm.id}`}
                              value={pm.chequeFecha}
                              onChange={(value) => updatePaymentMethod(pm.id, 'chequeFecha', value)}
                              placeholder="dd/mm/yyyy"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <div className="flex items-center justify-between pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addPaymentMethod}
                    className="flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    Agregar Método de Pago
                  </Button>
                  <div className="text-sm text-right">
                    {remainingAmount < -0.01 && acreditarResto ? (
                      <>
                        <span className="font-semibold">Sobrante a favor: </span>
                        <span className="text-teal-600">
                          ${Math.abs(remainingAmount).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </span>
                      </>
                    ) : remainingAmount < -0.01 && !acreditarResto && paymentMethods.some(
                      (pm) => String(pm.metodoPago).toLowerCase() === 'dolares',
                    ) ? (
                      <>
                        <span className="font-semibold">Vuelto (cambio): </span>
                        <span className="text-amber-600">
                          ${Math.abs(remainingAmount).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="font-semibold">Restante: </span>
                        <span className={remainingAmount > 0.01 ? 'text-red-500' : 'text-green-500'}>
                          ${remainingAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)} disabled={!!confirmingFactura}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmar} disabled={!!confirmingFactura}>
              {confirmingFactura ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {confirmType === "cae" ? "Obteniendo CAE..." : "Procesando..."}
                </>
              ) : (
                `Confirmar ${confirmType === "cae" ? "CAE" : "FX"}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={fechaModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setPendingConfirmPayload(null);
            setFechaAvisoYmd("");
          }
          setFechaModalOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{FECHA_VENTA_MODAL_TITULO}</AlertDialogTitle>
            <AlertDialogDescription>
              {mensajeSeCrearaConFecha(fechaAvisoYmd)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmarFechaVentaYEmitir()}>
              Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={showAsignacionModal}
        onOpenChange={(open) => {
          if (!open) cancelAsignacionDeposito();
        }}
      >
        <DialogContent
          className="z-[60] flex max-h-[85vh] flex-col overflow-hidden sm:max-w-lg"
          onInteractOutside={(e) => {
            if (asignacionSaving) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (asignacionSaving) e.preventDefault();
          }}
        >
          <DialogHeader className="shrink-0">
            <DialogTitle>¿Desde dónde retiran?</DialogTitle>
            <DialogDescription>
              Atajo factura + remito · elección agrupada por depósito
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 space-y-3 overflow-y-auto overscroll-contain py-1 pr-1">
            {asignacionPlan?.modo === "single_depot" ? (
              <RadioGroup
                value={
                  asignacionSelectedDep != null ? String(asignacionSelectedDep) : ""
                }
                onValueChange={(value) => {
                  const id = Number(value);
                  if (!Number.isFinite(id)) return;
                  setAsignacionSelectedDep(id);
                }}
                disabled={asignacionSaving}
                className="gap-2"
              >
                {(asignacionPlan.sharedCovering || []).map((dep) => {
                  const selected = asignacionSelectedDep === dep.id;
                  return (
                    <label
                      key={dep.id}
                      htmlFor={`auto-remito-dep-${dep.id}`}
                      className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 ${
                        selected
                          ? "border-sky-500 bg-sky-50 dark:bg-sky-900/20"
                          : "border-border bg-muted/40"
                      }`}
                    >
                      <RadioGroupItem
                        value={String(dep.id)}
                        id={`auto-remito-dep-${dep.id}`}
                        className="mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">
                          {asignacionPlan.sucursalNombre
                            ? `${asignacionPlan.sucursalNombre} · ${dep.nombre}`
                            : dep.nombre}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {sharedDepDetail(dep, asignacionOpciones)}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            ) : null}

            {asignacionPlan &&
            (asignacionPlan.modo === "grouped" || asignacionPlan.modo === "blocked")
              ? asignacionPlan.groups.map((group) => (
                  <div
                    key={group.depositoId}
                    className="space-y-1 rounded-md border border-border bg-muted/40 p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium">
                        {group.sucursalNombre
                          ? `${group.sucursalNombre} · ${group.depositoNombre}`
                          : group.depositoNombre}
                      </p>
                      <Badge variant="secondary">{countLabel(group.items)}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {productsLabel(group.items)}
                    </p>
                  </div>
                ))
              : null}

            {asignacionPlan?.modo === "blocked" ||
            (asignacionPlan?.insufficient?.length ?? 0) > 0 ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                <p className="font-medium">Sin stock — función bloqueada</p>
                <p className="mt-1 text-xs">
                  Producto(s) sin stock en ningún depósito:{" "}
                  {(asignacionPlan?.insufficient || [])
                    .map((i) => `«${i.nombre}»`)
                    .join(", ")}
                  . No se puede finalizar el remito. No se puede confirmar debido a falta
                  de stock de uno o más productos. La opción de auto finalizar remito de
                  ventas está inhabilitada; puede volver atrás y hacer la venta de manera
                  normal. La factura no se emitió.
                </p>
              </div>
            ) : null}
          </div>
          <DialogFooter className="shrink-0">
            <Button
              variant="outline"
              onClick={cancelAsignacionDeposito}
              disabled={asignacionSaving}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => void confirmarAsignacionYEmitir()}
              disabled={!asignacionConfirmOk || asignacionSaving}
              className="disabled:opacity-50 disabled:pointer-events-auto"
            >
              {asignacionSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Procesando…
                </>
              ) : (
                "Confirmar asignación"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete factura confirmation */}
      <AlertDialog open={!!facturaToDelete} onOpenChange={(open) => !open && setFacturaToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar factura pendiente</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que deseas eliminar la factura {facturaToDelete?.numero}? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFacturaToDelete(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDeleteFactura}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Nota de Crédito Dialog */}
      <Dialog open={!!facturaForNC} onOpenChange={(open) => !open && !generatingNC && setFacturaForNC(null)}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] min-w-0 overflow-x-hidden overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generar Nota de Crédito</DialogTitle>
            <DialogDescription>
              {ncInterna ? (
                <>
                  Factura <strong>{facturaForNC?.numero}</strong> — Se creará una NC-X interna con la fecha de esta factura.
                  No anula el CAE y no llama a AFIP. La NC fiscal sigue en el botón NC.
                </>
              ) : (
                <>
                  Factura <strong>{facturaForNC?.numero}</strong> — Se creará una NC-{(facturaForNC?.tipoComprobante || 'B').replace('NC-', '')}.
                  {' '}Solo se permite <strong>una</strong> NC (total o parcial) por factura; no habrá una segunda NC sobre el restante.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="min-w-0 space-y-4 py-2">
            {/* Full vs Partial toggle */}
            <div className="flex items-center gap-4 p-3 rounded-lg border border-input bg-muted/30">
              <Label className="text-sm font-medium flex-1">Tipo de Nota de Crédito</Label>
              <div className="flex gap-2">
                <Button
                  variant={!ncIsPartial ? "default" : "outline"}
                  size="sm"
                  className="text-xs"
                  onClick={() => {
                    setNcIsPartial(false);
                    setNcSelectedItems(buildNcItemMap(facturaForNC?.items as any[], { parcial: false }));
                  }}
                >
                  Total
                </Button>
                <Button
                  variant={ncIsPartial ? "default" : "outline"}
                  size="sm"
                  className="text-xs"
                  onClick={() => {
                    setNcIsPartial(true);
                    setNcSelectedItems(
                      buildNcItemMap(facturaForNC?.items as any[], { parcial: true }),
                    );
                  }}
                >
                  Parcial
                </Button>
              </div>
            </div>

            {/* Items table */}
            {facturaForNC?.items && facturaForNC.items.length > 0 && (
              <div className="border border-input rounded-lg overflow-x-auto">
                <table className="w-full min-w-[560px]">
                  <thead className="bg-muted">
                    <tr>
                      {ncIsPartial && (
                        <th className="py-2 px-3 text-center w-10">
                          <Checkbox
                            checked={Object.values(ncSelectedItems).every(v => v.selected)}
                            onCheckedChange={(checked) => {
                              const newMap = { ...ncSelectedItems };
                              Object.keys(newMap).forEach(k => {
                                newMap[Number(k)] = { ...newMap[Number(k)], selected: !!checked };
                              });
                              setNcSelectedItems(
                                patchNcVisualSelection(
                                  facturaForNC?.items as any[],
                                  newMap,
                                  Object.keys(newMap).map(Number),
                                  { selected: !!checked },
                                ),
                              );
                            }}
                          />
                        </th>
                      )}
                      <th className="text-left py-2 px-3 text-sm font-medium">Producto</th>
                      <th className="text-center py-2 px-3 text-sm font-medium w-24">
                        {ncIsPartial ? 'Cant. NC' : 'Cantidad'}
                      </th>
                      <th className="text-right py-2 px-3 text-sm font-medium">Precio</th>
                      <th className="text-right py-2 px-3 text-sm font-medium">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupComboVisualRows((facturaForNC.items as any[]) || []).map((row) => {
                      const legs = row.legs as any[];
                      const item = legs[0];
                      const itemIds = ncItemIds(legs);
                      const nombre = row.isCombo
                        ? comboVisualNombre(legs)
                        : facturaItemNombre(item);
                      const cantOriginal = Number(item.cantidad || 0);
                      const cantRestante = row.isCombo
                        ? ncComboRestante(legs)
                        : ncCantidadRestante(item);
                      const cantPendiente = row.isCombo
                        ? ncComboPendiente(legs)
                        : ncCantidadPendienteEntrega(item);
                      const cantSugerida = row.isCombo
                        ? ncComboSugerida(legs, ncIsPartial)
                        : ncCantidadSugerida(item, ncIsPartial);
                      const precio = row.isCombo
                        ? comboVisualPrecioSum(legs)
                        : parseFloat(item.precio_unitario || item.precioUnitario || 0);
                      const sel = itemIds.length
                        ? ncSelectedItems[itemIds[0]]
                        : ncSelectedItems[item.id];
                      const isSelected = itemIds.length
                        ? itemIds.every((id) => ncSelectedItems[id]?.selected)
                        : (sel ? sel.selected : cantSugerida > 0);
                      const cantNC = sel ? sel.cantidad : cantSugerida;
                      const lineExhausted = cantRestante <= 0;
                      const subtotalFinal = ncVisualLineSubtotal(legs, isSelected ? cantNC : 0);

                      return (
                        <tr
                          key={row.key}
                          className={`border-t border-input ${ncIsPartial && (!isSelected || lineExhausted) ? 'opacity-40' : ''}`}
                        >
                          {ncIsPartial && (
                            <td className="py-2 px-3 text-center">
                              <Checkbox
                                checked={isSelected && !lineExhausted}
                                disabled={lineExhausted}
                                onCheckedChange={(checked) => {
                                  setNcSelectedItems((prev) =>
                                    patchNcVisualSelection(
                                      facturaForNC?.items as any[],
                                      prev,
                                      itemIds.length ? itemIds : [item.id],
                                      {
                                        selected: !!checked,
                                        cantidad: prev[itemIds[0] || item.id]?.cantidad ?? cantSugerida,
                                      },
                                    ),
                                  );
                                }}
                              />
                            </td>
                          )}
                          <td className="py-2 px-3 text-sm">
                            <div>{nombre}</div>
                            {ncIsPartial && (
                              <div className="text-[10px] text-muted-foreground">
                                {cantPendiente < cantRestante
                                  ? `No entregado remito: ${cantPendiente} (restante factura: ${cantRestante})`
                                  : cantRestante < cantOriginal
                                  ? `Restante: ${cantRestante} de ${cantOriginal} unid.`
                                  : `Original: ${cantOriginal} unid.`}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {ncIsPartial && isSelected && !lineExhausted ? (
                              <NumberField
                                min={0}
                                max={cantRestante}
                                step="any"
                                value={cantNC}
                                onChange={(e) => {
                                  let val = parseFloat(e.target.value) || 0;
                                  if (val > cantRestante) val = cantRestante;
                                  if (val < 0) val = 0;
                                  setNcSelectedItems((prev) =>
                                    patchNcVisualSelection(
                                      facturaForNC?.items as any[],
                                      prev,
                                      itemIds.length ? itemIds : [item.id],
                                      { selected: true, cantidad: val },
                                    ),
                                  );
                                }}
                                className="h-8 w-20 text-center mx-auto"
                              />
                            ) : (
                              <span className="text-sm">{lineExhausted ? 0 : cantNC}</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-sm text-right">
                            ${precio.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-2 px-3 text-sm text-right font-medium">
                            {isSelected
                              ? `$${subtotalFinal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                              : '-'
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-muted border-t-2 border-input">
                    <tr>
                      <td colSpan={ncIsPartial ? 4 : 3} className="py-2 px-3 text-sm font-semibold text-right">
                        Total NC:
                      </td>
                      <td className="py-2 px-3 text-sm text-right font-bold">
                        ${(() => {
                          let total = 0;
                          groupComboVisualRows((facturaForNC?.items as any[]) || []).forEach((row) => {
                            const legs = row.legs as any[];
                            const ids = ncItemIds(legs);
                            const firstId = ids[0] ?? (legs[0] as any)?.id;
                            const sel = ncSelectedItems[firstId];
                            if (!sel?.selected) return;
                            total += ncVisualLineSubtotal(legs, sel.cantidad);
                          });
                          return total.toLocaleString('es-AR', { minimumFractionDigits: 2 });
                        })()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Credit toggle */}
            <div className="flex items-center justify-between gap-4 py-3 px-1">
              <Label htmlFor="nc-credito" className="flex-1 text-sm cursor-pointer">
                Acreditar sobrante a saldo a favor
              </Label>
              <Switch
                id="nc-credito"
                checked={ncSaldoHabilitado && ncEsCreditoCliente}
                disabled={!ncSaldoHabilitado}
                onCheckedChange={setNcEsCreditoCliente}
              />
            </div>
            <p className="text-xs text-muted-foreground px-1 -mt-2">
              {ncSaldoHabilitado
                ? (ncEsCreditoCliente
                  ? 'El sobrante queda como crédito del cliente y no se devuelve plata.'
                  : 'Se devuelve el dinero.')
                : NC_SALDO_CF_COPY}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFacturaForNC(null)} disabled={generatingNC}>
              Cancelar
            </Button>
            <Button
              disabled={generatingNC || (ncIsPartial && !Object.values(ncSelectedItems).some(v => v.selected && v.cantidad > 0))}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={async () => {
                if (!facturaForNC?.id || (ncInterna ? !onGenerarNCX : !onGenerarNC)) return;
                const origenId = facturaForNC.id;
                setGeneratingNC(true);
                try {
                  let itemsPayload: Array<{ id: number; cantidad: number }> | undefined;
                  if (ncIsPartial) {
                    itemsPayload = ncPayloadFromSelection(
                      facturaForNC.items as any[],
                      ncSelectedItems,
                    );
                  }
                  const acreditarSaldo = ncSaldoHabilitado && ncEsCreditoCliente;
                  const mark = (setter: React.Dispatch<React.SetStateAction<Set<number>>>) => {
                    setter((prev) => {
                      const next = new Set(prev);
                      next.add(origenId);
                      return next;
                    });
                  };
                  const tipoOrigen = (
                    facturaForNC.tipoComprobante || facturaForNC.tipoFactura || ""
                  ).toUpperCase();
                  const esCae = tipoOrigen === "A" || tipoOrigen === "B";
                  if (ncInterna) {
                    await onGenerarNCX!(origenId, acreditarSaldo, itemsPayload);
                  } else {
                    await onGenerarNC!(origenId, acreditarSaldo, itemsPayload);
                  }
                  mark(setNcEmitidasIds);
                  if (esCae || ncInterna) mark(setNcXEmitidasIds);
                  setFacturaForNC(null);
                } catch {
                  // El handler ya muestra el toast. No se marca la fila si falló.
                } finally {
                  setGeneratingNC(false);
                }
              }}
            >
              {generatingNC ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generando...
                </>
              ) : (
                `Generar ${ncInterna ? 'NC-X' : 'NC'}${ncIsPartial ? ' Parcial' : ''}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}